
import { useState, useEffect } from 'react';
import { DocumentSubmission, useDocumentSubmissions } from '@/hooks/useDocumentSubmissions';
import { DocumentHeader } from './detail/DocumentHeader';
import { DocumentMainContent } from './detail/DocumentMainContent';
import { DocumentInfo } from './detail/DocumentInfo';
import { ReviewActions } from './detail/ReviewActions';

interface DocumentDetailViewProps {
  document: DocumentSubmission;
  onBack: () => void;
  onApprove: (documentId: string) => void;
  onReject: (documentId: string, reason: string) => void;
}

export function DocumentDetailView({ 
  document, 
  onBack, 
  onApprove,
  onReject
}: DocumentDetailViewProps) {
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const { getDocumentUrl, updateDocumentStatus, isUpdating } = useDocumentSubmissions();

  useEffect(() => {
    const loadDocumentUrl = async () => {
      try {
        setIsLoadingPreview(true);
        const url = await getDocumentUrl(document.file_path);
        setDocumentUrl(url);
      } catch (error) {
        console.error('Error loading document URL:', error);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    loadDocumentUrl();
  }, [document, getDocumentUrl]);

  const handleDownload = async () => {
    if (documentUrl) {
      const a = window.document.createElement('a');
      a.href = documentUrl;
      a.download = document.file_name;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
    }
  };

  const handleApprove = () => {
    onApprove(document.id);
  };

  const handleReject = () => {
    onReject(document.id, 'Does not meet requirements');
  };

  return (
    <div className="space-y-6">
      <DocumentHeader 
        onBack={onBack}
        onDownload={handleDownload}
        onApprove={handleApprove}
        onReject={handleReject}
        status={document.status}
        documentUrl={documentUrl}
        isUpdating={isUpdating}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <DocumentMainContent
            document={document}
            documentUrl={documentUrl}
            isLoadingPreview={isLoadingPreview}
            onDownload={handleDownload}
          />
        </div>

        <div className="space-y-6">
          <DocumentInfo
            document={document}
            onDownload={handleDownload}
          />

          {document.status === 'pending' && (
            <ReviewActions
              onApprove={handleApprove}
              onReject={handleReject}
              isUpdating={isUpdating}
            />
          )}
        </div>
      </div>
    </div>
  );
}
